import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Check, ChevronDown, Sparkles } from 'lucide-react';
import { getAvatarColor } from '../../utils/colorUtils';

/** Empty string is the sentinel for "Auto-assign — let the backend pick". */
const AUTO_VALUE = '';

/**
 * Caller-shaped option for the picker. Both `CreateBookingModal` and
 * `EditEventModal` already have arrays of users with this shape; territory
 * names should be pre-resolved by the caller (we don't have access to the
 * tenant territory map here).
 */
export interface PreferredProviderOption {
  id: string;
  name?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  photoUrl?: string | null;
  /** Pre-resolved territory display names for territories mode. */
  territoryNames?: string[];
  /**
   * Pre-resolved identity color (see `getSpacedAvatarColors`). Callers
   * rendering a fixed roster pass spaced colors so teammates stay visually
   * distinct; otherwise we fall back to the hashed per-user color.
   */
  color?: string | null;
  /**
   * When set, a non-selectable section header with this text is rendered in
   * the menu just above this option (e.g. "Not on this booking form").
   */
  sectionLabel?: string;
}

interface PreferredProviderDropdownProps {
  value: string;
  options: PreferredProviderOption[];
  onChange: (id: string) => void;
  /** When true, prepends an "Auto-assign" option as the first menu item. */
  showAutoOption?: boolean;
  /** Override the default "Auto-assign" header text. */
  autoLabel?: string;
  /** Subtitle under the Auto option (e.g. "Round-robin between Mike, Sara"). */
  autoSubtitle?: string;
  /**
   * If provided, the matching user gets a subtle "currently assigned" tag —
   * useful in reschedule contexts where one user is already on the
   * appointment.
   */
  currentlyAssignedUserId?: string;
  /**
   * Render-only mode (used for Solo forms where the assignment is fixed).
   * Removes the chevron, no menu opens, no keyboard handling.
   */
  disabled?: boolean;
  ariaLabel?: string;
  placeholder?: string;
}

export const formatProviderName = (opt: PreferredProviderOption): string =>
  opt.name ||
  `${opt.firstName ?? ''} ${opt.lastName ?? ''}`.trim() ||
  opt.email ||
  'Unnamed user';

const formatInitials = (opt: PreferredProviderOption): string => {
  const fi = (opt.firstName ?? '').trim()[0];
  const li = (opt.lastName ?? '').trim()[0];
  const both = `${fi ?? ''}${li ?? ''}`.toUpperCase();
  if (both) return both;
  const first = (opt.name || opt.email || '?')[0];
  return (first ?? '?').toUpperCase();
};

export const ProviderAvatar: React.FC<{ option: PreferredProviderOption; size: 'sm' | 'md' | 'lg' }> = ({ option, size }) => {
  const px = size === 'sm' ? 'w-7 h-7 text-2xs' : size === 'md' ? 'w-8 h-8 text-11' : 'w-12 h-12 text-base';
  const seed = option.id || option.name || `${option.firstName ?? ''}${option.lastName ?? ''}`;
  const color = option.color || getAvatarColor(seed);
  if (option.photoUrl) {
    return (
      <img
        src={option.photoUrl}
        alt={formatProviderName(option)}
        className={`${px} rounded-full object-cover flex-shrink-0`}
      />
    );
  }
  return (
    <div
      className={`${px} rounded-full flex items-center justify-center flex-shrink-0`}
      style={{ backgroundColor: color }}
    >
      <span className="text-white font-semibold leading-none">{formatInitials(option)}</span>
    </div>
  );
};

const TerritoryChips: React.FC<{ names?: string[] }> = ({ names }) => {
  if (!names || names.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {names.map((n, i) => (
        <span
          key={`${n}-${i}`}
          className="inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
        >
          {n}
        </span>
      ))}
    </div>
  );
};

/**
 * Custom dropdown for the "Preferred Provider" picker on smart-assignment
 * forms (and the read-only "Assigned Provider" display on solo forms).
 * Shares behavior with `EventTypeDropdown` so both pickers feel the same.
 */
export const PreferredProviderDropdown: React.FC<PreferredProviderDropdownProps> = ({
  value,
  options,
  onChange,
  showAutoOption = false,
  autoLabel = 'Auto-assign',
  autoSubtitle,
  currentlyAssignedUserId,
  disabled = false,
  ariaLabel = 'Preferred provider',
  placeholder = 'Select a provider',
}) => {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  // Effective ordered list of selectable rows (Auto pseudo-option first when enabled).
  const rows = useMemo(() => {
    const list: Array<{ kind: 'auto' } | { kind: 'user'; option: PreferredProviderOption }> = [];
    if (showAutoOption) list.push({ kind: 'auto' });
    for (const opt of options) list.push({ kind: 'user', option: opt });
    return list;
  }, [options, showAutoOption]);

  const selectedRowIndex = useMemo(() => {
    if (value === AUTO_VALUE) {
      return rows.findIndex(r => r.kind === 'auto');
    }
    return rows.findIndex(r => r.kind === 'user' && r.option.id === value);
  }, [rows, value]);

  const selectedOption = useMemo(() => options.find(o => o.id === value) || null, [options, value]);
  const selectedIsAuto = value === AUTO_VALUE && showAutoOption;

  useEffect(() => {
    if (!open || disabled) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, disabled]);

  useEffect(() => {
    if (!open || disabled) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(prev => Math.min(rows.length - 1, prev < 0 ? 0 : prev + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(prev => Math.max(0, prev < 0 ? 0 : prev - 1));
      } else if (e.key === 'Home') {
        e.preventDefault();
        setHighlightedIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setHighlightedIndex(rows.length - 1);
      } else if (e.key === 'Enter' || e.key === ' ') {
        if (highlightedIndex >= 0 && highlightedIndex < rows.length) {
          e.preventDefault();
          const row = rows[highlightedIndex];
          const next = row.kind === 'auto' ? AUTO_VALUE : row.option.id;
          if (next !== value) onChange(next);
          setOpen(false);
          triggerRef.current?.focus();
        }
      } else if (e.key === 'Tab') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, highlightedIndex, rows, onChange, value, disabled]);

  useEffect(() => {
    if (!open) return;
    setHighlightedIndex(selectedRowIndex >= 0 ? selectedRowIndex : 0);
  }, [open, selectedRowIndex]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const item = listRef.current.querySelector<HTMLLIElement>(`[data-index="${highlightedIndex}"]`);
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, open]);

  const triggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (open || disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const renderTriggerContent = () => {
    if (selectedIsAuto) {
      return (
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-ink-900 truncate">{autoLabel}</div>
            {autoSubtitle && (
              <div className="text-[11.5px] text-ink-500 truncate">{autoSubtitle}</div>
            )}
          </div>
        </div>
      );
    }
    if (selectedOption) {
      const isCurrent = currentlyAssignedUserId && selectedOption.id === currentlyAssignedUserId;
      return (
        <div className="flex items-center gap-3 min-w-0">
          <ProviderAvatar option={selectedOption} size="md" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-sm font-semibold text-ink-900 truncate">{formatProviderName(selectedOption)}</span>
              {isCurrent && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium bg-ink-100 text-ink-700 border border-ink-200">
                  Currently assigned
                </span>
              )}
            </div>
            {selectedOption.territoryNames && selectedOption.territoryNames.length > 0 ? (
              <TerritoryChips names={selectedOption.territoryNames} />
            ) : selectedOption.email ? (
              <div className="text-[11.5px] text-ink-500 truncate">{selectedOption.email}</div>
            ) : null}
          </div>
        </div>
      );
    }
    return <span className="text-sm text-ink-400">{placeholder}</span>;
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup={disabled ? undefined : 'listbox'}
        aria-expanded={disabled ? undefined : open}
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={triggerKeyDown}
        className={`w-full text-left px-3 py-2.5 bg-accent-50 border-2 border-accent-300 rounded-xl focus:outline-none transition-all ${
          disabled
            ? 'opacity-90 cursor-default'
            : 'cursor-pointer hover:border-accent-400 focus:bg-surface focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500'
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">{renderTriggerContent()}</div>
          {!disabled && (
            <ChevronDown
              className={`w-4 h-4 text-accent-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
            />
          )}
        </div>
      </button>

      {open && !disabled && (
        <ul
          ref={listRef}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute z-50 mt-1 left-0 right-0 max-h-80 overflow-y-auto bg-surface border border-ink-200 rounded-xl shadow-xl py-1"
        >
          {rows.map((row, idx) => {
            const isHighlighted = idx === highlightedIndex;
            const isSelected = idx === selectedRowIndex;
            const baseRow = `px-3 py-2.5 cursor-pointer flex items-start gap-2 transition-colors ${
              isHighlighted ? 'bg-accent-50' : ''
            } ${isSelected ? 'bg-accent-100/70' : ''}`;
            if (row.kind === 'auto') {
              return (
                <li
                  key="__auto__"
                  data-index={idx}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onClick={() => {
                    if (value !== AUTO_VALUE) onChange(AUTO_VALUE);
                    setOpen(false);
                    triggerRef.current?.focus();
                  }}
                  className={baseRow}
                >
                  <div className="w-4 h-4 mt-0.5 flex-shrink-0 flex items-center justify-center">
                    {isSelected && <Check className="w-4 h-4 text-accent-600" />}
                  </div>
                  <div className="w-7 h-7 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-ink-900">{autoLabel}</div>
                    {autoSubtitle && (
                      <div className="text-[11.5px] text-ink-500 leading-snug mt-0.5">{autoSubtitle}</div>
                    )}
                  </div>
                </li>
              );
            }
            const opt = row.option;
            const isCurrent = currentlyAssignedUserId && opt.id === currentlyAssignedUserId;
            return (
              <React.Fragment key={opt.id}>
                {opt.sectionLabel && (
                  <li
                    role="presentation"
                    className="px-3 pt-2.5 pb-1 mt-1 border-t border-ink-100 text-2xs font-semibold uppercase tracking-wide text-ink-400"
                  >
                    {opt.sectionLabel}
                  </li>
                )}
                <li
                  data-index={idx}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onClick={() => {
                    if (opt.id !== value) onChange(opt.id);
                    setOpen(false);
                    triggerRef.current?.focus();
                  }}
                  className={baseRow}
                >
                  <div className="w-4 h-4 mt-0.5 flex-shrink-0 flex items-center justify-center">
                    {isSelected && <Check className="w-4 h-4 text-accent-600" />}
                  </div>
                  <ProviderAvatar option={opt} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-sm ${isSelected ? 'font-semibold' : 'font-medium'} text-ink-900`}>
                        {formatProviderName(opt)}
                      </span>
                      {isCurrent && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium bg-ink-100 text-ink-700 border border-ink-200">
                          Currently assigned
                        </span>
                      )}
                    </div>
                    {opt.territoryNames && opt.territoryNames.length > 0 ? (
                      <TerritoryChips names={opt.territoryNames} />
                    ) : opt.email ? (
                      <div className="text-[11.5px] text-ink-500 truncate mt-0.5">{opt.email}</div>
                    ) : null}
                  </div>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PreferredProviderDropdown;
